import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_DOMAIN } from 'consts';
import { makeApiRequest } from 'helpers';
import forEach from 'lodash/forEach';
import isPlainObject from 'lodash/isPlainObject';
import map from 'lodash/map';
import reject from 'lodash/reject';
import some from 'lodash/some';
import { useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { security } from 'shared';

import { maybeNotifyRunFinished, setNotificationNavigator } from '../notifications';

export type ChatRunStatus = 'running' | 'completed' | 'failed' | 'stopped';

export type ChatRun = {
  id: string;
  status: ChatRunStatus;
  error: string | null;
};

export type ChatSummary = {
  id: string;
  user_id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // Run fields are optional so older API responses (which omit them) still
  // type-check; absent means "no run info", which renders as idle + read.
  // Status of the chat's latest run; null when it has none.
  run_status?: ChatRunStatus | null;
  // The latest run finished after the owner last viewed the chat.
  unread?: boolean;
  // Only returned by GET /api/chats/:id.
  run?: ChatRun | null;
};

type ChatsPage = {
  results: ChatSummary[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
};

type ChatsInfiniteData = { pages: ChatsPage[]; pageParams: number[] };

const PAGE_SIZE = 100;
// Poll while a run is in flight so the sidebar spinner/unread dot and the chat
// page notice a detached (server-side) run finishing. No polling when idle.
const LIST_POLL_MS = 5000;
const DETAIL_POLL_MS = 3000;

export function useChats (search?: string, { enabled = true }: { enabled?: boolean } = {}) {
  const query = useInfiniteQuery<ChatsPage>({
    queryKey: ['chats', { q: search }],
    enabled,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(pageParam),
      });
      if (search) params.set('q', search);
      return makeApiRequest(`/api/chats?${params}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
    // react-query v5 passes the Query; returning false disables polling.
    refetchInterval: (q) =>
      some(q.state.data?.pages, (page) => some(page.results, { run_status: 'running' }))
        ? LIST_POLL_MS
        : false,
    // Keep polling while the tab is hidden: a run finishing is exactly what
    // a backgrounded tab needs to notice (react-query pauses intervals in
    // hidden tabs otherwise).
    refetchIntervalInBackground: true,
  });

  const chats = useMemo(
    () => query.data?.pages.flatMap((p) => p.results),
    [query.data],
  );

  return { ...query, chats };
}

/**
 * Desktop notification for ANY chat whose run finishes, not only one this tab
 * is streaming: a run followed after a reload, one started on another device,
 * one that ends while the reader is on a different page. The list is polled
 * while any chat runs (see useChats), so a `running` → `completed`/`failed`
 * transition between two polls is the signal. Mount once, somewhere that is
 * always on screen (the app sidebar).
 */
export function useRunCompletionNotifications (enabled: boolean) {
  const { chats } = useChats(undefined, { enabled });
  const previous = useRef<Map<string, ChatRunStatus | null | undefined>>(new Map());
  const history = useHistory();

  // Clicking a notification opens its chat without reloading the page.
  useEffect(() => {
    setNotificationNavigator((path) => history.push(path));
    return () => setNotificationNavigator(null);
  }, [history]);

  useEffect(() => {
    if (!chats) return;
    const seen = previous.current;
    const next = new Map<string, ChatRunStatus | null | undefined>();
    forEach(chats, (chat) => {
      next.set(chat.id, chat.run_status);
      const status = chat.run_status;
      if (seen.get(chat.id) === 'running' && (status === 'completed' || status === 'failed')) {
        maybeNotifyRunFinished(chat.id, status, chat.title);
      }
    });
    previous.current = next;
  }, [chats]);
}

// Metadata for a single chat (title etc.). The key shares the `['chats']`
// prefix so the rename/create invalidations below refetch it too.
export function useChatSummary (id?: string) {
  return useQuery<ChatSummary>({
    queryKey: ['chats', 'detail', id],
    queryFn: () => makeApiRequest(`/api/chats/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.run?.status === 'running' ? DETAIL_POLL_MS : false),
    refetchIntervalInBackground: true,
  });
}

// Marks a chat as viewed. The cache is patched optimistically so the dot goes
// at once, then refetched once the server has the marker.
export function useMarkChatRead () {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await security.getAccessTokenSilently()();
      const resp = await fetch(`${API_DOMAIN}/api/chats/${id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Failed to mark chat read: ${resp.status}`);
      return resp.json() as Promise<{ status: 'ok' }>;
    },
    onMutate: async (id) => {
      // Only this chat's detail entry and the list caches (`['chats', { q }]`)
      // are touched. Other `['chats', ...]` keys (e.g. quota) are left alone so
      // cancelling can't strand their in-flight fetches.
      const filters = {
        queryKey: ['chats'],
        predicate: ({ queryKey }: { queryKey: readonly unknown[] }) =>
          queryKey[1] === 'detail' ? queryKey[2] === id : isPlainObject(queryKey[1]),
      };
      // No cancelQueries here: the read marker fires the moment a response
      // finishes, which is also when the sidebar list is refetched for the
      // new title and run status; cancelling that fetch kept the stale
      // "New Chat / running" row. A response that lands with `unread: true`
      // is reconciled by the refetch in onSettled.
      // List caches are infinite-query data ({ pages }); the detail cache is a
      // bare ChatSummary. Patch each by shape.
      const queryCache = queryClient.getQueriesData<ChatsInfiniteData | ChatSummary>(filters);

      for (const [key, data] of queryCache) {
        if (!data || !isPlainObject(data)) continue;
        if ('pages' in data) {
          if (!some(data.pages, (page) => some(page.results, { id, unread: true }))) continue;
          queryClient.setQueryData<ChatsInfiniteData>(key, {
            ...data,
            pages: map(data.pages, (page) => ({
              ...page,
              results: map(page.results, (chat) => (chat.id === id ? { ...chat, unread: false } : chat)),
            })),
          });
        } else if (data.id === id && data.unread) {
          queryClient.setQueryData<ChatSummary>(key, { ...data, unread: false });
        }
      }
    },
    // Resync once the server has the marker (an unread chat is opened once,
    // so this is one list + one detail refetch, not a storm).
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });
}

export function useRenameChat () {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const token = await security.getAccessTokenSilently()();
      const resp = await fetch(`${API_DOMAIN}/api/chats/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title }),
      });
      if (!resp.ok) throw new Error(`Failed to rename chat: ${resp.status}`);
      return resp.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });
}

export function useDeleteChat () {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await security.getAccessTokenSilently()();
      const resp = await fetch(`${API_DOMAIN}/api/chats/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Failed to delete chat: ${resp.status}`);
      return resp.json();
    },
    // Optimistic removal from cache — iterate pages to find and remove the chat
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['chats'] });
      const queryCache = queryClient.getQueriesData<ChatsInfiniteData>({ queryKey: ['chats'] });

      for (const [key, data] of queryCache) {
        if (!data?.pages) continue;
        queryClient.setQueryData(key, {
          ...data,
          pages: map(data.pages, (page) => ({
            ...page,
            results: reject(page.results, { id }),
          })),
        });
      }

      return { queryCache };
    },
    onError: (_err, _id, context) => {
      // Restore all cached query data on error
      if (context?.queryCache) {
        for (const [key, data] of context.queryCache) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });
}
